// The MedSnapp Rounds lockup, set in type: "MedSnapp" and "Rounds" in Bricolage Grotesque, with an optional Pulse
// running under "Rounds" and ending in the beam head. (The supplied MedSnapp logo, cleaned up in web/public and drawn by
// MedSnappLogo, was too faint in the product; the user put the typographic lockup back on 9 Oct 2026 until a new logo is
// settled. The files and the component stay for the favicons, the link preview and the share card.)
// MedSnapp must never read tiny beside Rounds (user, 9 Oct 2026): in the compact lockup both words are 1 em, MedSnapp
// semibold and Rounds extrabold, both in full ink; stacked, MedSnapp sits at 0.55 em, bold, over Rounds.
//   full     stacked: MedSnapp over a heavy Rounds. Size it with font-size (text-5xl…).
//   compact  one line for headers: MedSnapp Rounds.
// One Pulse per screen: the line under "Rounds" is off unless `pulse` is set. Turn it on only where the screen shows
// no other Pulse. When on, it is live in the full lockup and a still trace in the compact one (so a header never
// moves while people are answering).
import { Pulse, type PulseMood } from './Pulse';

export interface WordmarkProps {
  variant?: 'full' | 'compact';
  className?: string;
  /** 'ink' on light grounds; 'inverse' (white) on teal or the monitor. */
  tone?: 'ink' | 'inverse';
  /** Draw the Pulse under "Rounds" (default: no). Only where the screen shows no other Pulse. */
  pulse?: boolean;
  /** Mood of the Pulse under "Rounds", when `pulse` is set. */
  mood?: PulseMood;
  /** Animate the Pulse, when `pulse` is set (default: full yes, compact no). */
  animated?: boolean;
  /** Kept for callers from before; has no effect (there is no mark beside the words). */
  mark?: boolean;
}

export function Wordmark({ variant = 'full', className = '', tone = 'ink', pulse: showPulse = false, mood = 'steady', animated }: WordmarkProps) {
  const inverse = tone === 'inverse';
  const ink = inverse ? 'text-white' : 'text-ink';
  const pulse = inverse ? 'currentColor' : 'var(--brand)';

  if (variant === 'compact') {
    return (
      <span className={`inline-flex items-center leading-none ${className}`} role="img" aria-label="MedSnapp Rounds">
        <span aria-hidden="true" className="flex items-baseline gap-[0.28em] font-display">
          <span data-part="wordmark-medsnapp" className={`font-semibold tracking-[-0.015em] ${ink}`} style={{ fontSize: '1em' }}>MedSnapp</span>
          <span data-part="wordmark-rounds" className={`relative font-extrabold tracking-[-0.035em] ${ink}`}>
            Rounds
            {/* R waves about 0.2 em under the baseline: an underline with a rhythm, clear of the letters. A flex box, so the
                Pulse sits exactly where it is put (an inline box would ride the line's strut). */}
            {showPulse && (
              <span className={`absolute left-0 right-[-0.3em] top-[calc(100%-0.02em)] flex ${inverse ? 'text-white' : 'text-brand'}`}>
                <Pulse width="100%" height="0.46em" mood={mood} animated={animated ?? false} color={pulse} fade={false} />
              </span>
            )}
          </span>
        </span>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-end leading-none ${className}`} role="img" aria-label="MedSnapp Rounds">
      <span aria-hidden="true" className="inline-flex flex-col font-display">
        <span data-part="wordmark-medsnapp" className={`ml-[0.04em] font-bold tracking-[-0.015em] ${ink}`} style={{ fontSize: '0.55em', lineHeight: 1.1 }}>MedSnapp</span>
        <span data-part="wordmark-rounds" className={`font-extrabold tracking-tighter ${ink}`} style={{ lineHeight: 0.92 }}>Rounds</span>
        {/* Tucked up so the R waves almost touch the letters: the rhythm belongs to the word. A flex box, so the Pulse
            does not ride the 1 em line strut of this font size. */}
        {showPulse && (
          <span className={`flex ${inverse ? 'text-white' : 'text-brand'}`} style={{ marginTop: '-0.14em', marginRight: '-0.24em' }}>
            <Pulse width="100%" height="0.44em" mood={mood} animated={animated ?? true} color={pulse} />
          </span>
        )}
      </span>
    </span>
  );
}
